import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { AlertService } from './alert.service';
import { EditingFlowService } from './editing-flow.service';

export const SYSTEM_EMAILS_SETTINGS_URL = '/client/settings/invitations';

/**
 * What a send/resend says when its email was blocked. Green, because the action
 * itself worked; the banner (when due) explains the email (Logan, 2026-10-08).
 */
export const BLOCKED_SEND_MESSAGE = 'Invoice status updated';

/**
 * RF-2548: tells a staff member that an email their action fired was dropped
 * because the client has System Emails turned off — without spamming them.
 *
 * Automatic: rf-api flags any blocked email on the response
 * (X-Emails-Blocked / X-Show-Blocked-Banner) and RfHttpInterceptor calls
 * reportBlocked(), so no endpoint or component has to be wired up for it.
 *
 * - No separate warning toast (Logan, 2026-10-08: the banner is the only
 *   signal). Callers whose success toast says the email went out use success(),
 *   which swaps it for a green "Invoice status updated" on a flagged response,
 *   so the UI never claims an email was sent when it wasn't.
 * - The banner (styled like the new-release banner, with More info / Settings)
 *   only shows when the API says so: rf-api throttles it to once a day per user,
 *   server-side, so it holds across devices. Nothing is stored in the browser.
 * - Like the new-release banner, it waits while a drawer or dialog is open.
 *
 * Background sends the setting blocks never reach the UI.
 */
@Injectable({
    providedIn: 'root',
})
export class SystemEmailsWarningService {
    /** True while a banner is up or waiting to show — never stack two. */
    private bannerPending = false;

    /**
     * Set while a flagged response is being delivered to its subscribers, so
     * their success() toast can change its wording. Cleared on the next macrotask —
     * after every synchronous subscriber of that response has run.
     */
    private justBlocked = false;

    constructor(
        private toastr: ToastrService,
        private router: Router,
        private alertService: AlertService,
        private editingFlow: EditingFlowService,
    ) {}

    /** Called by RfHttpInterceptor for a response that flagged blocked emails. */
    reportBlocked(showBanner: boolean): void {
        this.justBlocked = true;
        setTimeout(() => (this.justBlocked = false));

        this.maybeShowBanner(showBanner);
    }

    /**
     * A success toast that claims an email went out. When the same response
     * reported the email was blocked, says "Invoice status updated" instead.
     */
    success(message: string): void {
        this.toastr.success(this.justBlocked ? BLOCKED_SEND_MESSAGE : message);
    }

    private maybeShowBanner(show: boolean): void {
        if (!show || this.bannerPending) {
            return;
        }

        this.bannerPending = true;

        if (!this.editingFlow.isOpen()) {
            this.showBanner();
            return;
        }

        this.editingFlow.whenClosed().subscribe(() => this.showBanner());
    }

    private showBanner(): void {
        const toast = this.toastr.show(
            `
            <div class="system-emails-toast-content">
                <div class="system-emails-toast-message">Unable to send. Enable system-generated emails in Invitation Settings.</div>
                <div class="system-emails-toast-actions">
                    <a href="javascript:void(0)" class="system-emails-toast-more" data-test-system-emails-more-info>More info</a>
                    <a href="javascript:void(0)" class="system-emails-toast-settings" data-test-system-emails-settings>Settings <i class="bi bi-arrow-right"></i></a>
                </div>
            </div>
            `,
            '',
            {
                disableTimeOut: true,
                closeButton: true,
                tapToDismiss: false,
                positionClass: 'toast-bottom-right',
                enableHtml: true,
                toastClass: 'ngx-toastr system-emails-toast',
            },
        );

        toast.onHidden.subscribe(() => (this.bannerPending = false));

        // ngx-toastr renders the message as HTML, so the buttons are wired by
        // event delegation (same as the new-release banner). bannerPending
        // guarantees this is the only System Emails banner on the page.
        toast.onShown.subscribe(() => {
            document
                .querySelector('.system-emails-toast')
                ?.addEventListener('click', (e) => {
                    const link = (e.target as HTMLElement).closest('a');
                    if (!link) {
                        return;
                    }
                    e.preventDefault();

                    if (link.classList.contains('system-emails-toast-more')) {
                        this.alertService.requestOpenPanel();
                    } else {
                        this.router.navigateByUrl(SYSTEM_EMAILS_SETTINGS_URL);
                    }

                    this.toastr.remove(toast.toastId);
                });
        });
    }
}
